import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Eye, Heart, Scale, Sparkles, RotateCw } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { PageTransition } from "@/components/PageTransition";
import { AnimatedMeshGradient } from "@/components/AnimatedMeshGradient";
import { FlippingCard } from "@/components/FlippingCard";
import { useT } from "@/lib/i18n";
import { ABOUT } from "@/constants/testIds";

const VAL_META = [
  { num: "01", icon: Eye, testId: ABOUT.value1 },
  { num: "02", icon: Heart, testId: ABOUT.value2 },
  { num: "03", icon: Scale, testId: ABOUT.value3 },
];

// Front face: the value title as a clean cover.
function ValueFront({ v, hint }) {
  const Icon = v.icon;
  return (
    <div className="nxx-flip-surface flex h-full flex-col p-7">
      <div className="flex items-center justify-between">
        <Icon size={22} strokeWidth={1.5} style={{ color: "#BEE5E0" }} />
        <span className="font-display" style={{ fontSize: 22, color: "rgba(190,229,224,0.85)" }}>
          {v.num}<span style={{ color: "#FF6857" }}>.</span>
        </span>
      </div>
      <div className="mt-auto">
        <div
          className="font-display"
          style={{ fontSize: 34, lineHeight: 1.05, color: "#F7F6F2", fontWeight: 500, letterSpacing: "-0.01em" }}
        >
          {v.title}
        </div>
        <div className="mt-3 h-px" style={{ background: "#FF6857", width: 32 }} />
        <div
          className="mt-4 flex items-center gap-2 font-ui"
          style={{ fontSize: 11, letterSpacing: "0.14em", color: "rgba(190,229,224,0.55)" }}
        >
          <RotateCw size={12} />
          <span>{hint}</span>
        </div>
      </div>
    </div>
  );
}

// Back face: the description, revealed on flip.
function ValueBack({ v, cta }) {
  return (
    <div className="nxx-flip-surface flex h-full flex-col justify-between p-7">
      <div>
        <div className="kicker" style={{ color: "rgba(190,229,224,0.75)", fontSize: 12 }}>{v.title}</div>
        <p className="font-display mt-4" style={{ color: "#F7F6F2", lineHeight: 1.35, fontSize: 21, fontWeight: 500, letterSpacing: "-0.01em" }}>
          {v.body}
        </p>
      </div>
      <Link
        to="/markets"
        className="inline-flex items-center gap-2 font-ui transition-opacity hover:opacity-80"
        style={{ fontSize: 13, color: "#BEE5E0", fontWeight: 600, letterSpacing: "0.03em" }}
      >
        <span>{cta}</span>
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}

export default function About() {
  const t = useT();
  const VALUES = VAL_META.map((m, i) => ({ ...m, ...t.about.values[i] }));
  return (
    <PageTransition>
      {/* HERO LEDE */}
      <section className="relative paper-grain overflow-hidden" style={{ background: "var(--nexxis-bg)" }}>
        <AnimatedMeshGradient intensity={1} />
        <div className="hero-fade-bottom" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-8 pt-36 sm:pt-44 pb-16">
          <Reveal><div className="kicker mb-6">{t.about.heroKicker}</div></Reveal>
          <div className="grid grid-cols-12 gap-8 items-end">
            <Reveal as="div" className="col-span-12 lg:col-span-8">
              <h1
                className="font-display"
                style={{
                  fontSize: "clamp(40px, 5.5vw, 76px)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  color: "var(--nexxis-ink)",
                  fontWeight: 500,
                }}
              >
                {t.about.heroTitle}
              </h1>
            </Reveal>
            <Reveal as="div" delay={0.08} className="col-span-12 lg:col-span-4">
              <div className="h-px mb-5" style={{ background: "#FF6857", width: 48 }} />
              <p className="font-ui" style={{ color: "var(--nexxis-ink-muted)", lineHeight: 1.7, fontSize: 15 }}>
                {t.about.heroBody}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="relative" style={{ background: "var(--nexxis-bg-alt)" }}>
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-20 sm:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal as="div" data-testid={ABOUT.mission}>
              <div
                className="nxx-card-tilt nxx-card--glow h-full p-8 lg:p-10 rounded-[14px]"
                style={{ background: "#0D5F5E", color: "#F7F6F2" }}
              >
                <Compass size={22} strokeWidth={1.4} style={{ color: "#BEE5E0" }} />
                <div className="kicker mt-6" style={{ color: "rgba(190,229,224,0.7)" }}>{t.about.mission}</div>
                <h3
                  className="font-display mt-3"
                  style={{
                    fontSize: "clamp(26px, 2.8vw, 36px)",
                    lineHeight: 1.15,
                    letterSpacing: "-0.012em",
                    color: "#F7F6F2",
                    fontWeight: 500,
                  }}
                >
                  {t.about.missionText}
                </h3>
              </div>
            </Reveal>
            <Reveal as="div" delay={0.08} data-testid={ABOUT.vision}>
              <div
                className="nxx-card-tilt nxx-card--glow h-full p-8 lg:p-10 rounded-[14px]"
                style={{ background: "var(--nexxis-bg)", border: "1px solid var(--nexxis-border-strong)" }}
              >
                <Sparkles size={22} strokeWidth={1.4} style={{ color: "#0D5F5E" }} />
                <div className="kicker mt-6">{t.about.vision}</div>
                <h3
                  className="font-display mt-3"
                  style={{
                    fontSize: "clamp(26px, 2.8vw, 36px)",
                    lineHeight: 1.15,
                    letterSpacing: "-0.012em",
                    color: "var(--nexxis-ink)",
                    fontWeight: 500,
                  }}
                >
                  {t.about.visionText}
                </h3>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="relative paper-grain" style={{ background: "var(--nexxis-bg)" }} data-testid={ABOUT.story}>
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-20 sm:py-24">
          <div className="grid grid-cols-12 gap-8">
            <Reveal as="div" className="col-span-12 lg:col-span-4">
              <div className="kicker mb-4">{t.about.storyKicker}</div>
              <h2
                className="font-display"
                style={{
                  fontSize: "clamp(28px, 3.4vw, 44px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.015em",
                  color: "var(--nexxis-ink)",
                  fontWeight: 500,
                }}
              >
                {t.about.storyTitle}
              </h2>
            </Reveal>
            <Reveal as="div" delay={0.08} className="col-span-12 lg:col-span-8">
              <div className="space-y-5 font-ui" style={{ color: "var(--nexxis-ink)", lineHeight: 1.8, fontSize: 16 }}>
                {t.about.storyParas.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section data-testid={ABOUT.values} className="relative" style={{ background: "var(--nexxis-bg-alt)" }}>
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-20 sm:py-24">
          <div className="max-w-[640px] mb-12">
            <Reveal>
              <div className="kicker mb-5">{t.about.valuesKicker}</div>
              <h2
                className="font-display"
                style={{
                  fontSize: "clamp(32px, 4vw, 52px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.018em",
                  color: "var(--nexxis-ink)",
                  fontWeight: 500,
                }}
              >
                {t.about.valuesTitle}
              </h2>
              <p className="font-ui mt-4" style={{ color: "var(--nexxis-ink-muted)", lineHeight: 1.7 }}>
                {t.about.valuesLede}
              </p>
            </Reveal>
          </div>
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {VALUES.map((v) => (
              <StaggerItem key={v.num}>
                <FlippingCard
                  testId={v.testId}
                  height={280}
                  className="nxx-flip-coral"
                  frontContent={<ValueFront v={v} hint={t.about.flipHint} />}
                  backContent={<ValueBack v={v} cta={t.about.valueCta} />}
                />
              </StaggerItem>
            ))}
          </Stagger>

          <div className="mt-16">
            <Reveal>
              <Link
                to="/markets"
                className="inline-flex items-center gap-3 rounded-[10px] px-5 py-3 font-ui text-[13px]"
                style={{
                  background: "#0D5F5E",
                  color: "#F7F6F2",
                  letterSpacing: "0.04em",
                  fontWeight: 600,
                }}
              >
                <span>See our markets coverage</span>
                <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
